import {
  ChakraBox,
  ChakraFlex,
  DropdownV2,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  TextAreaInputV2,
  useToastMessage,
} from '@equip.health/ui';
import { useCreateAiSummaryReviewMutation } from '~/store/services/notes/aiGeneratedNoteSessionSummary';
import { AiSummaryReviewRating } from '~/lib/types/notes/AiSummaryReviewRating';
import { RATING_REASONS } from '~/lib/constants/RatingReasons';
import { useState } from 'react';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AISummaryEvents } from '~/analytics/aiSummaryEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

type Props = {
  appointmentId: string;
  isOpen: boolean;
  onSubmit: () => void;
  onClose: () => void;
};

const TOP_LEVEL_ID = 'negative-feedback-ai-summary';

const RATING_REASON_MENU_OPTIONS = RATING_REASONS.map((reason) => ({
  label: reason.label,
  value: `${reason.id}`,
}));
export const PatientNoteAiSummaryNegativeFeedbackModal = ({
  appointmentId,
  isOpen,
  onClose,
  onSubmit,
}: Props) => {
  const [createAiSummaryReview, { isLoading: isCreateAiSummaryReviewLoading }] =
    useCreateAiSummaryReviewMutation();

  const [ratingReason, setRatingReason] = useState<string | null>(null);
  const [ratingComments, setRatingComments] = useState('');
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();

  const trackEventAndClose = (aiSummaryEvent: AISummaryEvents) => {
    trackEvent(aiSummaryEvent, {
      Source: AnalyticsSourceValues.SESSION_GUIDE,
    });
    onClose();
  };

  const submitFeedback = () => {
    createAiSummaryReview({
      itemCode: 'APPOINTMENT',
      itemId: appointmentId,
      ratingId: AiSummaryReviewRating.NEGATIVE,
      ratingReasonId: parseInt(ratingReason, 10),
      comment: ratingComments || undefined,
    })
      .then(() => {
        createToast({
          icon: FAIconName.checkRegular,
          id: 'submitted-negative-feedback-ai-summary-success',
          text: 'Thank you for your feedback',
          variant: 'success',
        });
      })
      .catch(() => {
        createToast({
          icon: FAIconName.XmarkRegular,
          id: 'submitted-negative-feedback-ai-summary-failure',
          text: 'An unexpected error occurred submitting your feedback',
          variant: 'critical',
        });
      });
    onSubmit();
    trackEventAndClose(AISummaryEvents.aiSummaryNegativeFeedbackSubmitted);
  };
  return (
    <ModalV2
      isOpen={isOpen}
      onClose={() =>
        trackEventAndClose(AISummaryEvents.aiSummaryNegativeFeedbackClosed)
      }
      size="lg"
      title="Provide feedback"
    >
      <ChakraFlex flexDirection="column" gap="24px">
        <Text variant="body">
          We’re always working to improve the quality of our AI Summaries and
          your input is essential. If you have any additional information to
          provide, please do so below.
        </Text>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}-rating-reason-group`}
          label="What didn't you like about the summary?"
        >
          <DropdownV2
            ariaLabel="Reason for rating thumbs down"
            id={`${TOP_LEVEL_ID}-rating-reason`}
            menuOptions={RATING_REASON_MENU_OPTIONS}
            onOptionClick={(option) => {
              setRatingReason(option);
            }}
            value={ratingReason}
          />
        </InputGroupV2>
        {/*necessary because the current inputgroup component clips the "optional" text on the right hand side*/}
        <ChakraBox marginX="1px">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}-comments-group`}
            isRequired={false}
            label="Do you have any additional comments?"
          >
            <TextAreaInputV2
              ariaLabel="Enter additional comments here"
              characterLimit={420}
              id={`${TOP_LEVEL_ID}-comments`}
              onChange={(val) => setRatingComments(val)}
              value={ratingComments}
            />
          </InputGroupV2>
        </ChakraBox>
        <ChakraFlex justifyContent="space-between">
          <FireflyButton
            id={`${TOP_LEVEL_ID}-cancel`}
            isDisabled={isCreateAiSummaryReviewLoading}
            leftIcon={FAIconName.XmarkRegular}
            onClick={() =>
              trackEventAndClose(
                AISummaryEvents.aiSummaryNegativeFeedbackCancelled,
              )
            }
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}-submit`}
            isDisabled={!ratingReason}
            isLoading={isCreateAiSummaryReviewLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={submitFeedback}
          >
            Submit feedback
          </FireflyButton>
        </ChakraFlex>
      </ChakraFlex>
    </ModalV2>
  );
};
